import { produce } from 'immer';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Icons from '../../../../../../common/widgets/Icons';
import { resources } from '../../../../resources/resources';
import EditEmployee from './Components/EditEmployeeForm';
import EmployeeDetailsTabNav from './Components/EmployeeDetailsTabNav';
import StateHistoryGrid from './Components/StateHistoryGrid';
import Transactions from './Components/Transactions';
import { EmployeeDetailsTabs } from './types';

import moment from 'moment';
import { showErrorToast } from '../../../../../../common/global/helpers';
import Button from '../../../../../../common/widgets/Button';
import { employeeAdminService } from '../../../../services/employee-admin';
import AddTransactionDialog from './Dialogs/AddTransactionDialog';

export interface EmployeeDetailsState {
  employeeId: number;
  activeTab: EmployeeDetailsTabs;
  showAddTransactionDialog: boolean;
  isGeneratinReport: boolean;
  needTransactionReload: boolean;
}

export default function EmployeeDetailsPage() {
  const { employeeId: employeeIdParam } = useParams();
  const navigate = useNavigate();
  const { state: passedState } = useLocation();
  const { t: translate } = useTranslation();
  const [state, setState] = useState<EmployeeDetailsState>(
    {} as EmployeeDetailsState,
  );

  const handleAddTransaction = () => {
    setState(
      produce(d => {
        d.showAddTransactionDialog = true;
      }),
    );
  };

  const handleDownloadTransactionReport = async () => {
    setState(
      produce(d => {
        d.isGeneratinReport = true;
      }),
    );
    const fileName = `transactions-${passedState.employeeName}-${moment(
      new Date(),
    ).format('DD-MM-YYYY')}`;
    const resp = await employeeAdminService.downloadBudgetTransactionsReport(
      state.employeeId,
      fileName,
    );
    setState(
      produce(d => {
        d.isGeneratinReport = false;
      }),
    );
    if (resp.hasErrors) {
      showErrorToast(translate(resources.errors.cannotGenerateFile));
    }
  };

  const handleReloadDone = () => {
    setState(
      produce(d => {
        d.needTransactionReload = false;
      }),
    );
  };

  const onTabChange = (tab: EmployeeDetailsTabs) => {
    setState(
      produce(d => {
        d.activeTab = tab;
      }),
    );
  };

  useEffect(() => {
    const employeeId = Number(employeeIdParam);
    setState(
      produce(d => {
        d.employeeId = employeeId;
        d.activeTab = EmployeeDetailsTabs.GeneralTab;
      }),
    );
  }, [employeeIdParam]);

  return (
    <div className="panel padding-xl employee-panel">
      <div className="btn-panel-back-wrapper">
        <button className="btn btn-panel-back" onClick={() => navigate(-1)}>
          <span className="btn-text">
            {translate(resources.admin.employees.details.crumbText)}
          </span>
        </button>
      </div>
      <h1>{passedState.employeeName}</h1>

      {/*Employee tabs*/}
      <div className="employee-details-header">
        <div className="acc-tabs-container">
          <EmployeeDetailsTabNav
            activeTab={state.activeTab}
            onTabChange={onTabChange}
          />
        </div>
        <div className="employee-details-actions">
          {state.activeTab === EmployeeDetailsTabs.TransactionTab && (
            <Button
              className="btn btn-solid btn-sm"
              onClick={handleAddTransaction}
              icon={<Icons.AddTransaction />}
            >
              <span className="btn-text">
                {translate(
                  resources.admin.employees.details.trasactions.actions.add,
                )}
              </span>
            </Button>
          )}
          {(state.activeTab === EmployeeDetailsTabs.TransactionTab ||
            state.needTransactionReload) && (
            <Button
              className="btn btn-solid btn-sm"
              onClick={handleDownloadTransactionReport}
              loading={state.isGeneratinReport}
              icon={<Icons.DownloadFile />}
            >
              <span className="btn-text">
                {translate(
                  resources.admin.employees.details.trasactions.actions
                    .download,
                )}
              </span>
            </Button>
          )}
        </div>
      </div>

      {state.activeTab === EmployeeDetailsTabs.GeneralTab && (
        <EditEmployee employeeId={state.employeeId} />
      )}
      {state.activeTab === EmployeeDetailsTabs.TransactionTab && (
        <Transactions
          employeeId={state.employeeId}
          needReload={state.needTransactionReload}
          reloadDone={handleReloadDone}
        />
      )}
      {state.activeTab === EmployeeDetailsTabs.StateHistoryTab && (
        <StateHistoryGrid employeeId={state.employeeId} />
      )}
      {state.showAddTransactionDialog && (
        <AddTransactionDialog
          employeeId={state.employeeId}
          close={() =>
            setState(
              produce(d => {
                d.showAddTransactionDialog = false;
              }),
            )
          }
          triggerReload={() =>
            setState(
              produce(d => {
                d.needTransactionReload = true;
              }),
            )
          }
        />
      )}
    </div>
  );
}
